import { DateObjectSchema } from '../../util/time'
import { DoubleSchema, StringListSchema } from '../../util/schema'
import { ManualZoneRunSchema } from '../zone/schemas'

/**
 * A module containing schema validation and documentation related to Schedules
 * @module ducks/schedule/schemas
 */

// Constants //////////////////////////////////////////////////////////////////

const ScheduleTypes = ['FIXED', 'FLEX_DAILY', 'FLEX_MONTHLY']
const SunTimes = ['SUNRISE', 'SUNSET']
const OddDays = ['ODD_DAY_DISABLED', 'ODD', 'EVEN']
const DaysOfWeek = [
  'MONDAY',
  'TUESDAY',
  'WEDNESDAY',
  'THURSDAY',
  'FRIDAY',
  'SATURDAY',
  'SUNDAY'
]
const ExpressionTypes = [
  'EQUALS',
  'LESS_THAN',
  'GREATER_THAN',
  'LESS_THAN_OR_EQUAL',
  'GREATER_THAN_OR_EQUAL',
  'BETWEEN_EXCLUSIVE',
  'BETWEEN_INCLUSIVE',
  'IN'
]

// Data ///////////////////////////////////////////////////////////////////////

export const TimeSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    hour: {
      //int32 hour = 1;
      type: 'number'
    },
    minute: {
      //int32 minute = 2;
      type: 'number'
    },
    second: {
      //int32 second = 3;
      type: 'number'
    },
    millis: {
      //int32 millis = 4;
      type: 'number'
    }
    /* eslint-enable camelcase */
  },
  additionalProperties: false
}

/** A JSONSchema definition for a schedule type */
export const ScheduleTypeSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'string',
  enum: ScheduleTypes
}

export const DayOfWeekExpressionSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    expression_type: {
      // ExpressionType expression_type = 1;
      type: 'string',
      enum: ExpressionTypes
    },
    parameter: {
      // repeated DayOfWeek parameter = 2;
      type: 'array',
      items: {
        type: 'string',
        enum: DaysOfWeek
      }
    }
    /* eslint-enable camelcase */
  },
  additionalProperties: false
}

/** A JSONSchema definition for a "sun time" */
export const SunTimeSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'string',
  enum: SunTimes
}

/** A JSONSchema definition for a "time list" */
export const TimeListSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    time: {
      type: 'array',
      items: TimeSchema
    }
  },
  additionalProperties: false,
  required: ['time']
}

/** A JSONSchema definition for a schedule criteria */
export const ScheduleCriteriaSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    schedule_type: ScheduleTypeSchema, // 1
    start_date: DateObjectSchema, // Date start_date = 2
    end_date: DateObjectSchema, // Date end_date = 3
    start_time_set: TimeListSchema, //   TimeList start_time_set = 4;
    start_sun_time: SunTimeSchema, //   SunTime start_sun_time = 5;
    end_time_set: TimeListSchema, //   TimeList end_time_set = 6;
    end_sun_time: SunTimeSchema, //   SunTime end_sun_time = 7;
    rain_delay_enabled: {
      // bool rain_delay_enabled = 8
      type: 'boolean'
    },
    freeze_delay_enabled: {
      // bool freeze_delay_enabled = 9
      type: 'boolean'
    },
    wind_delay_enabled: {
      // bool wind_delay_enabled = 10
      type: 'boolean'
    },
    climate_skip: {
      // bool climate_skip = 11
      type: 'boolean'
    },
    seasonal_shift: {
      // bool seasonal_shift = 12
      type: 'boolean'
    },
    smart_cycle: {
      // bool smart_cycle = 13
      type: 'boolean'
    },
    cycle_soak: {
      // bool cycle_soak = 14
      type: 'boolean'
    },
    soak_time: {
      // int32 soak_time = 15 (seconds)
      type: 'number'
    },
    cycle_time: {
      // int32 cycle_time = 16 (seconds)
      type: 'number'
    },
    zone_delay_time: {
      // int32 zone_delay_time = 17 (seconds)
      type: 'number'
    },
    override_location_restrictions: {
      // bool override_location_restrictions = 18
      type: 'boolean'
    }
    /* eslint-enable camelcase */
  },
  required: [
    'schedule_type',
    'start_date',
    'rain_delay_enabled',
    'freeze_delay_enabled',
    'wind_delay_enabled',
    'climate_skip'
  ],
  // TODO: need to have a oneOf for smartCycle/CycleSoak
  oneOf: [
    { required: ['start_time_set'] },
    { required: ['start_sun_time'] },
    { required: ['end_time_set'] },
    { required: ['end_sun_time'] }
  ],
  additionalProperties: false
}

/** A JSONSchema definition for a schedule type */
export const ScheduleZoneInfoSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    device_id: {
      // string device_id = 1
      type: 'string'
    },
    zone_id: {
      // string zone_id = 2
      type: 'string'
    },
    order_id: {
      // int32 order_id = 3
      type: 'number'
    },
    watering_time: {
      // int32 watering_time = 4
      type: 'number'
    },
    flex_aggression_coefficient: DoubleSchema, // double flex_aggression_coefficient = 5
    flex_runtime_coefficient: DoubleSchema // double flex_runtime_coefficient = 6
    /* eslint-enable camelcase */
  },
  required: [
    'device_id',
    'zone_id',
    'order_id',
    'watering_time',
    'flex_aggression_coefficient',
    'flex_runtime_coefficient'
  ],
  additionalProperties: false
}

/** A JSONSchema definition for a schedule */
export const ScheduleSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    id: {
      // string id = 1
      type: 'string'
    },
    name: {
      // string name = 2
      type: 'string'
    },
    schedule_criteria: ScheduleCriteriaSchema, // ScheduleCriteria schedule_criteria = 3
    /* eslint-disable max-len */
    schedule_restriction_criteria: ScheduleRestrictionCriteriaSchema, // ScheduleRestrictionCriteria schedule_restriction_criteria = 4;
    /* eslint-enable max-len */
    zone_info: {
      // repeated ScheduleZoneInfo zone_info = 5
      type: 'array',
      items: ScheduleZoneInfoSchema
    },
    enabled: {
      // bool enabled = 6;
      type: 'boolean'
    },
    created: {
      // google.protobuf.Timestamp created = 7
      type: 'number'
    },
    updated: {
      // google.protobuf.Timestamp updated = 8
      type: 'number'
    }
    /* eslint-enable camelcase */
  },
  additionalProperties: false
}

export const ScheduleRestrictionCriteriaSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase, max-len */
    day_of_week_constraint: {
      // repeated DayOfWeekExpression day_of_week_constraint = 1; // when we are allowed to water
      type: 'array',
      items: {
        type: 'string',
        enum: DaysOfWeek
      }
    },
    odd_day_constraint: {
      //OddDay odd_day_constraint = 3; // odd or even
      type: 'string',
      enum: OddDays
    },
    minimum_interval_days: {
      //int32 minimum_interval_days = 4; // minimum number of days between waterings
      type: 'number'
    },
    minimum_interval_hours: {
      //int32 minimum_interval_hours = 5; // minimum number of hours between waterings
      type: 'number'
    }
    /* eslint-enable camelcase, max-len */
  },
  additionalProperties: false,
  maxProperties: 1
}

// Requests ///////////////////////////////////////////////////////////////////

/** A JSONSchema definition for a schedule create request */
export const CreateScheduleRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    name: {
      // string name = 1
      type: 'string'
    },
    schedule_criteria: ScheduleCriteriaSchema, // ScheduleCriteria schedule_criteria = 2
    /* eslint-disable max-len */
    schedule_restriction_criteria: ScheduleRestrictionCriteriaSchema, // ScheduleRestrictionCriteria schedule_restriction_criteria = 3
    /* eslint-enable max-len */
    zone_info: {
      // repeated ScheduleZoneInfo zone_info = 4
      type: 'array',
      items: ScheduleZoneInfoSchema
    },
    enabled: {
      // google.protobuf.BoolValue enabled = 5;
      type: 'boolean'
    }
    /* eslint-enable camelcase */
  },
  required: ['name', 'schedule_criteria', 'zone_info'],
  additionalProperties: false
}

/** A JSONSchema definition for a schedule create request */
export const SchedulePreviewRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    name: {
      // string name = 1
      type: 'string'
    },
    schedule_criteria: ScheduleCriteriaSchema, // ScheduleCriteria schedule_criteria = 2
    /* eslint-disable max-len */
    schedule_restriction_criteria: ScheduleRestrictionCriteriaSchema, // ScheduleRestrictionCriteria schedule_restriction_criteria = 3
    /* eslint-enable max-len */
    zone_info: {
      // repeated ScheduleZoneInfo zone_info = 4
      type: 'array',
      items: ScheduleZoneInfoSchema
    }
    /* eslint-enable camelcase */
  },
  required: ['name', 'schedule_criteria', 'zone_info'],
  additionalProperties: false
}

/** A JSONSchema definition for a schedule get request */
export const GetSchedulesRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    schedule_id: StringListSchema, // StringList schedule_id = 1;
    zone_id: StringListSchema, // StringList zone_id = 2;
    device_id: StringListSchema, // StringList device_id = 3;
    location_id: StringListSchema // StringList location_id = 4;
    /* eslint-enable camelcase */
  },
  additionalProperties: false,
  oneOf: [
    { required: ['schedule_id'] },
    { required: ['device_id'] },
    { required: ['zone_id'] },
    { required: ['location_id'] }
  ]
}

/** A JSONSchema definition for a schedule update request */
export const UpdateScheduleRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    schedule_id: {
      // string schedule_id = 1;
      type: 'string'
    },
    name: {
      // google.protobuf.StringValue name = 2;
      type: 'string'
    },
    /* eslint-disable max-len */
    schedule_criteria: ScheduleCriteriaSchema, // ScheduleCriteria schedule_criteria = 3; //this must be the complete replace, no partial updates to criteria are supported
    schedule_restriction_criteria: ScheduleRestrictionCriteriaSchema, // ScheduleRestrictionCriteria schedule_restriction_criteria = 4; //this must be the complete replace, no partial updates to criteria are supported
    /* eslint-enable max-len */
    zone_info_to_add_or_update: {
      // repeated ScheduleZoneInfo zone_info_to_add_or_update = 5;
      type: 'array',
      items: ScheduleZoneInfoSchema
    },
    zone_ids_to_remove: {
      // repeated string zone_ids_to_remove = 6;
      type: 'array',
      items: {
        type: 'string'
      }
    },
    enabled: {
      // google.protobuf.BoolValue enabled = 7;
      type: 'boolean'
    }
    /* eslint-enable camelcase */
  },
  required: ['schedule_id', 'name', 'schedule_criteria'],
  additionalProperties: false
}

/** A JSONSchema definition for a schedule delete request */
export const DeleteSchedulesRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    schedule_id: {
      type: 'string'
    }
    /* eslint-enable camelcase */
  },
  additionalProperties: false,
  required: ['schedule_id']
}

/** A JSONSchema definition for a set manual schedule request */
export const SetManualScheduleRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    device_id: {
      // string device_id = 1
      type: 'string'
    },
    runs: {
      // repeated ManualZoneRun runs = 2
      type: 'array',
      items: ManualZoneRunSchema
    }
    /* eslint-enable camelcase */
  },
  required: ['device_id', 'runs'],
  additionalProperties: false
}

/** A JSONSchema definition for a stop watering request */
export const StopWateringRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    device_id: {
      // string device_id = 1
      type: 'string'
    }
    /* eslint-enable camelcase */
  },
  required: ['device_id'],
  additionalProperties: false
}

/** A JSONSchema definition for a zone skip request */
export const SkipForwardZoneRunRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    device_id: {
      // string device_id = 1
      type: 'string'
    }
    /* eslint-enable camelcase */
  },
  required: ['device_id'],
  additionalProperties: false
}

/** A JSONSchema definition for a zone skip (reverse) request */
export const SkipBackwardZoneRunRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    device_id: {
      // string device_id = 1
      type: 'string'
    }
    /* eslint-enable camelcase */
  },
  required: ['device_id'],
  additionalProperties: false
}

/** A JSONSchema definition for a zone pause request */
export const PauseZoneRunRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    device_id: {
      // string device_id = 1
      type: 'string'
    },
    seconds_paused: {
      // int32 seconds_paused = 2
      type: 'number'
    }
    /* eslint-enable camelcase */
  },
  required: ['device_id', 'seconds_paused'],
  additionalProperties: false
}

/** A JSONSchema definition for a zone resume request */
export const ResumeZoneRunRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    device_id: {
      // string device_id = 1
      type: 'string'
    },
    code: {
      type: 'string'
    }

    /* eslint-enable camelcase */
  },
  required: ['device_id'],
  additionalProperties: false
}

/** A JSONSchema definition for a zone extend run request */
export const ExtendZoneRunRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    device_id: {
      // string device_id = 1
      type: 'string'
    },
    seconds_extended: {
      // int32 seconds_extended = 2
      type: 'number'
    }
    /* eslint-enable camelcase */
  },
  required: ['device_id', 'seconds_extended'],
  additionalProperties: false
}

/** A JSONSchema definition for a schedule copy request */
export const CopyScheduleRequestSchema = {
  $schema: 'http://json-schema.org/draft-07/schema#',
  type: 'object',
  properties: {
    /* eslint-disable camelcase */
    schedule_id: {
      // string schedule_id = 1
      type: 'string'
    }
    /* eslint-enable camelcase */
  },
  required: ['schedule_id'],
  additionalProperties: false
}
